/*
  Japyara product page (loaded only on product pages by snippets/product.liquid).
  - Gallery arrows: two buttons on the main image that click Broadcast's own previous / next
    thumbnail, so image switching stays Broadcast's (product-thumbs / product-images).
  - Wishlist: sets --jp-wishlist-top so the Wishlist by Square button (the app's own element,
    left in place in the DOM) is shown as a round heart beside the title. Nothing in the app changes.
  - Tabs (sections/jp-product-tabs): ARIA tabs with arrow / Home / End keys.
  - Related products (sections/jp-product-related): loads Shopify's recommendations into the section,
    then wires the prev / next arrows to scroll the row. The ingredients and video sliders reuse the same arrows.
  - Video feedbacks (sections/jp-product-videos): click-to-play, one video at a time.
*/
(() => {
  const ARROW = (dir) =>
    `<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="${
      dir === 'prev' ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'
    }" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  const initArrows = (root) => {
    const slides = root.querySelector('.product__slides');
    const thumbs = [...root.querySelectorAll('.product__thumb [data-thumb-link]')];
    if (!slides || thumbs.length < 2 || slides.querySelector('.jp-pdp-arrow')) return;

    const step = (delta) => {
      const current = thumbs.findIndex((link) => link.closest('.product__thumb').classList.contains('is-active'));
      const next = (Math.max(current, 0) + delta + thumbs.length) % thumbs.length;
      thumbs[next].click();
      // Keep the active thumbnail visible in the sideways-scrolling row (scrolls the row only, never the page)
      const holder = root.querySelector('.product__thumbs__holder');
      const thumb = thumbs[next].closest('.product__thumb');
      if (holder && thumb) {
        const left = thumb.offsetLeft - holder.offsetLeft;
        if (left < holder.scrollLeft || left + thumb.offsetWidth > holder.scrollLeft + holder.clientWidth) {
          holder.scrollTo({ left, behavior: 'smooth' });
        }
      }
    };

    [['prev', -1, 'Previous image'], ['next', 1, 'Next image']].forEach(([dir, delta, label]) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `jp-pdp-arrow jp-pdp-arrow--${dir}`;
      button.setAttribute('aria-label', label);
      button.innerHTML = ARROW(dir);
      button.addEventListener('click', (event) => {
        event.preventDefault();
        step(delta);
      });
      slides.appendChild(button);
    });
  };

  const placeWishlist = (root) => {
    const title = root.querySelector('[data-jp-pdp-title]');
    const box = root.querySelector('.form__width');
    if (!title || !box) return;
    const top = title.getBoundingClientRect().top - box.getBoundingClientRect().top;
    const offset = Math.max(0, Math.round(top + (title.offsetHeight - 44) / 2));
    box.style.setProperty('--jp-wishlist-top', `${offset}px`);
  };

  const init = (root) => {
    initArrows(root);
    placeWishlist(root);
    // The app injects its button after load and fonts can change the title height
    window.addEventListener('load', () => placeWishlist(root));
    window.addEventListener('resize', () => placeWishlist(root));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => placeWishlist(root));
  };

  /* ---------- Description / Care Instructions tabs (sections/jp-product-tabs.liquid) ---------- */
  const initTabs = (root) => {
    if (root.dataset.jpTabsReady) return;
    root.dataset.jpTabsReady = 'true';
    const tabs = [...root.querySelectorAll('[data-jp-tab]')];
    const select = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const panel = root.querySelector('#' + t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
    };
    // Phones: accordion rows (CSS shows them below 750px and hides the tab list); all start collapsed
    root.querySelectorAll('[data-jp-accordion-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        toggle.setAttribute('aria-expanded', toggle.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
      });
    });
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(tab, false));
      tab.addEventListener('keydown', (event) => {
        const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
        if (!(event.key in keys)) return;
        event.preventDefault();
        const k = keys[event.key];
        const next = k === 'first' ? 0 : k === 'last' ? tabs.length - 1 : (i + k + tabs.length) % tabs.length;
        select(tabs[next], true);
      });
    });
  };

  /* ---------- Related products (sections/jp-product-related.liquid) ---------- */
  const initRelatedNav = (root) => {
    const track = root.querySelector('[data-jp-related-track]');
    const prev = root.querySelector('[data-jp-related-prev]');
    const next = root.querySelector('[data-jp-related-next]');
    if (!track || !prev || !next) return;
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max;
      root.classList.toggle('jp-pdp-related--static', max <= 0);
    };
    const step = (dir) => {
      const item = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: dir * ((item ? item.offsetWidth : track.clientWidth) + gap), behavior: 'smooth' });
    };
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
  };

  const initRelated = (root) => {
    if (root.dataset.jpRelatedReady) return;
    root.dataset.jpRelatedReady = 'true';
    const url = root.dataset.url;
    if (!url) {
      initRelatedNav(root);
      return;
    }
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-jp-related]');
        if (!fresh) return;
        root.innerHTML = fresh.innerHTML;
        initRelatedNav(root);
      })
      .catch(() => {});
  };

  const initAll = () => {
    document.querySelectorAll('product-info.jp-pdp').forEach(init);
    document.querySelectorAll('[data-jp-tabs]').forEach(initTabs);
    document.querySelectorAll('[data-jp-related]').forEach(initRelated);
    // Ingredients and video sliders (sections/jp-product-ingredients, jp-product-videos) use the same prev / next scrolling
    document.querySelectorAll('[data-jp-ingredients], [data-jp-videos]').forEach((root) => {
      if (root.dataset.jpSliderReady) return;
      root.dataset.jpSliderReady = 'true';
      initRelatedNav(root);
    });
    document.querySelectorAll('[data-jp-videos]').forEach(initVideos);
  };

  /* ---------- Video feedbacks (sections/jp-product-videos.liquid) ----------
     The play button starts that video with its controls shown and pauses any other playing one;
     the button comes back when the video ends. */
  const initVideos = (root) => {
    if (root.dataset.jpVideosReady) return;
    root.dataset.jpVideosReady = 'true';
    root.querySelectorAll('[data-jp-video]').forEach((box) => {
      const video = box.querySelector('video');
      const play = box.querySelector('[data-jp-video-play]');
      if (!video || !play) return;
      play.addEventListener('click', () => {
        document.querySelectorAll('[data-jp-video] video').forEach((other) => {
          if (other !== video && !other.paused) other.pause();
        });
        box.classList.add('is-playing');
        video.controls = true;
        const started = video.play();
        if (started && started.catch) started.catch(() => {});
        video.focus({ preventScroll: true });
      });
      video.addEventListener('ended', () => {
        box.classList.remove('is-playing');
        video.controls = false;
        play.focus({ preventScroll: true });
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);

  /* Easy Subscriptions' starting option (Subscribe & Save or Regular purchase) is the app's own
     "default purchase option" setting. A page restored by the browser's Back / Forward cache keeps the
     shopper's last choice, together with the app's hidden cart fields. */
})();
